// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import Axios from 'axios';
import { injectable } from 'inversify';

import Platform, {
  PlatformDTO,
  DroneManufacturer,
  DroneModel,
  CameraItem,
  LenseItem,
  GimbalItem,
  GPSProvider
} from '@shared/models/platform';
import BaseService from '@core/services/base';
import { ListRequestParams } from '@shared/types/services';
import { PLATFORM_FIELDS } from '@shared/constants/platform';
import { REQUEST_PARAMS, ActivityFilter } from '@shared/constants/services';

@injectable()
export default class PlatformsService extends BaseService<PlatformDTO, Platform> {
  static diToken = Symbol('platforms-service');

  constructor() {
    super({ domainArea: 'platforms', Model: Platform });
  }

  getListQuery({ pagination, sorting, filtering }: ListRequestParams) {
    return `
      {
        platforms(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize},
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
          sort: { field: ${sorting?.field}, type: ${sorting?.type}},
          ${REQUEST_PARAMS.filtering.fields.activityFilter}: ${filtering?.activityFilter ?? ActivityFilter.all},
          ${REQUEST_PARAMS.filtering.fields.isBlocked}: ${filtering?.isBlocked ?? null}
        ) {
          items {
            ${PLATFORM_FIELDS.id},
            ${PLATFORM_FIELDS.companyId},
            ${PLATFORM_FIELDS.serialNumber},
            ${PLATFORM_FIELDS.status},
            ${PLATFORM_FIELDS.isBlocked},
            ${PLATFORM_FIELDS.isActive},
            ${PLATFORM_FIELDS.drone.name} {
              ${PLATFORM_FIELDS.drone.fields.manufacturerName}
              ${PLATFORM_FIELDS.drone.fields.name}
            },
            ${PLATFORM_FIELDS.payloadHardware.name} {
              ${PLATFORM_FIELDS.payloadHardware.fields.cameras},
              ${PLATFORM_FIELDS.payloadHardware.fields.gimbals},
              ${PLATFORM_FIELDS.payloadHardware.fields.lenses},
            }
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  deactivatePlatform(id: PlatformDTO['id'], isBlocked: PlatformDTO['isBlocked']) {
    const url = `${this.urlPrefix}/${id}`;

    return Axios.patch(url, { isActive: !isBlocked });
  }

  async getDroneModels(id: DroneManufacturer['id']): Promise<Array<DroneModel>> {
    const url = `/drone-models/${id}`;
    const { data } = await Axios.get(url);

    return data;
  }

  async getDroneManufacturers(): Promise<Array<DroneManufacturer>> {
    const url = '/drone-manufacturers';
    const { data } = await Axios.get(url);

    return data;
  }

  async getCameras(): Promise<Array<CameraItem>> {
    const url = '/cameras';
    const { data } = await Axios.get(url);

    return data;
  }

  async getGimbals(): Promise<Array<GimbalItem>> {
    const url = '/gimbals';
    const { data } = await Axios.get(url);

    return data;
  }

  async getLenses(): Promise<Array<LenseItem>> {
    const url = '/lenses';
    const { data } = await Axios.get(url);

    return data;
  }

  async getGpsProviders(): Promise<Array<GPSProvider>> {
    const url = '/gps-providers';
    const { data } = await Axios.get(url);

    return data;
  }
}
